import { useState, useRef, useEffect } from "react";
import { createLead } from "../../lib/api";
import Reveal from "./Reveal";
import { toast } from "sonner";
import { ChevronDown, X, MessageCircle, Mail, Clock } from "lucide-react";
import { logger } from "../../lib/logger";
import { PLACEHOLDER_INSTAGRAM, PLACEHOLDER_INSTAGRAM_HANDLE, placeholder, VALIDATION_EMAIL_INVALID, DEFAULT_PHONE_PREFIX, EMAIL_REGEX, DEFAULT_EMPTY_LEAD } from "../../lib/constants";

function SearchableSelect({ value, onChange, options, placeholder }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  const filtered = options.filter(o =>
    !search || o.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div data-id="searchable-select-wrapper" ref={ref} className="relative mt-3">
      <button data-id="searchable-select-trigger"
        type="button"
        onClick={() => { setOpen(!open); setSearch(""); }}
        className="editorial-field w-full text-left bg-transparent flex items-center justify-between"
      >
        <span data-id={`searchable-select-value-${value || 'none'}`} className={value ? "text-softblack" : "text-warmgray/60"}>{value || placeholder}</span>
        <ChevronDown data-id="searchable-select-chevron" size={14} className={`text-warmgray transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div data-id="searchable-select-dropdown" className="absolute top-full left-0 mt-1 w-full bg-white border border-fendi rounded-lg shadow-xl z-50 overflow-hidden">
          <div data-id="searchable-select-search-wrapper" className="p-2 border-b border-fendi/50">
            <input data-id="searchable-select-search-input"
              autoFocus
              type="text"
              placeholder={placeholder("short")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full text-sm px-3 py-2 bg-fendi/10 rounded-md outline-none focus:bg-fendi/20 transition-colors"
            />
          </div>
          <div data-id="searchable-select-options-list" className="max-h-40 overflow-y-auto">
            {filtered.length === 0 ? (
              <p data-id="searchable-select-no-results" className="px-3 py-4 text-sm text-warmgray text-center">{placeholder("short")}</p>
            ) : (
              filtered.map((o) => (
                <button
                  key={o}
                  data-id={`searchable-select-option-${o}`}
                  type="button"
                  onClick={() => { onChange(o); setOpen(false); setSearch(""); }}
                  className={`w-full text-left px-3 py-2.5 text-sm hover:bg-fendi/20 transition-colors ${
                    o === value ? "bg-fendi/30" : ""
                  }`}
                >
                  {o}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StyledSelect({ value, onChange, options, placeholder, ...rest }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function handleClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);
  return (
    <div ref={ref} className="relative mt-3" {...rest}>
      <button data-id="styled-select-trigger" type="button" onClick={() => setOpen(!open)}
        className="editorial-field w-full text-left bg-transparent flex items-center justify-between">
        <span data-id={`styled-select-value-${value || 'none'}`} className={value ? "text-softblack" : "text-warmgray/60"}>{value || placeholder}</span>
        <ChevronDown data-id="styled-select-chevron" size={14} className={`text-warmgray transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-id="styled-select-dropdown" className="absolute top-full left-0 mt-1 w-full bg-white border border-fendi rounded-lg shadow-xl z-50 overflow-hidden">
          <div data-id="styled-select-options-list" className="max-h-50 overflow-y-auto">
            <button data-id="styled-select-option-placeholder" type="button" onClick={() => { onChange(""); setOpen(false); }}
              className={`w-full text-left px-3 py-2.5 text-sm hover:bg-fendi/20 transition-colors ${!value ? "bg-fendi/30" : ""}`}>
              {placeholder}
            </button>
            {options.map((o) => (
              <button key={o} data-id={`styled-select-option-${o}`} type="button" onClick={() => { onChange(o); setOpen(false); }}
                className={`w-full text-left px-3 py-2.5 text-sm hover:bg-fendi/20 transition-colors ${o === value ? "bg-fendi/30" : ""}`}>
                {o}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PrefixSelector({ value, onChange, prefixes }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const ref = useRef(null);

  const selected = (prefixes && prefixes.length > 0) 
    ? (prefixes.find((p) => p.code === value) || prefixes[0])
    : { flag: "🏳️", code: "+00", country: "" };

  const q = (search || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
  const filtered = !q ? (prefixes || []) : (prefixes || []).filter(p => {
    const target = (`${p.code} ${p.country} ${p.flag}`).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    let qi = 0;
    for (let si = 0; si < target.length && qi < q.length; si++) {
      if (target[si] === q[qi]) qi++;
    }
    return qi === q.length;
  });

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div data-id="prefix-selector-wrapper" ref={ref} className="relative">
      <button data-id="prefix-selector-trigger"
        type="button"
        onClick={() => { setOpen(!open); setSearch(""); }}
        className="flex items-center gap-1.5 px-3 bg-transparent text-sm hover:border-champagne transition-colors w-full text-left"
        style={{ padding: "14px 12px 12px", fontSize: "15px", fontFamily: "inherit", borderBottom: "1px solid #8E857A" }}
      >
        <span data-id={`prefix-selector-flag-${selected.code}`} className="text-base">{selected.flag}</span>
        <span data-id={`prefix-selector-code-${selected.code}`} className="text-softblack font-medium">{selected.code}</span>
        <ChevronDown data-id="prefix-selector-chevron" size={14} className={`text-warmgray transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div data-id="prefix-selector-dropdown" className="absolute bottom-full left-0 mb-2 w-64 bg-white border border-fendi rounded-lg shadow-xl z-50 overflow-hidden">
          <div data-id="prefix-selector-search-wrapper" className="p-2 border-b border-fendi/50">
            <div data-id="prefix-selector-search-inner" className="relative">
              <input data-id="prefix-selector-search-input"
                autoFocus
                type="text"
                placeholder={placeholder("short")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full text-sm px-3 py-2 bg-fendi/10 rounded-md outline-none focus:bg-fendi/20 transition-colors"
              />
              {search && (
                <button data-id="prefix-selector-search-clear"
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-warmgray hover:text-softblack"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
          <div data-id="prefix-selector-options-list" className="max-h-52 overflow-y-auto">
            {filtered.length === 0 ? (
              <p data-id="prefix-selector-no-results" className="px-3 py-4 text-sm text-warmgray text-center">{placeholder("short")}</p>
            ) : (
              filtered.map((p) => (
                <button
                  key={p.code + p.country}
                  data-id={`prefix-selector-option-${p.code}`}
                  type="button"
                  onClick={() => { onChange(p.code); setOpen(false); setSearch(""); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-fendi/20 transition-colors ${
                    p.code === value ? "bg-fendi/30" : ""
                  }`}
                >
                  <span data-id={`prefix-selector-option-flag-${p.code}`} className="text-lg">{p.flag}</span>
                  <span data-id={`prefix-selector-option-country-${p.code}`} className="flex-1 text-left text-softblack">{p.country}</span>
                  <span data-id={`prefix-selector-option-code-${p.code}`} className="text-warmgray font-mono text-xs">{p.code}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Contact({ sectionContent, formServices, contactMethods, formPrefixes, globals, whatsappNumber, uiStrings, elementVisibility, sectionId }) {
  const cs = uiStrings?.contact_section || {};
  const eyebrow = cs.eyebrow || sectionContent?.eyebrow || uiStrings?.general?.contact_eyebrow || "";
  const headline = cs.headline || sectionContent?.headline || uiStrings?.general?.contact_headline || "";
  const description = sectionContent?.description || cs.description || uiStrings?.general?.contact_description || "";
  const contactEmail = globals?.contact_email || "";
  const locations = uiStrings?.general?.locations || globals?.locations || "";
  const whatsapp = whatsappNumber || globals?.whatsapp_number || "";
  const instagramUrl = globals?.instagram_url || PLACEHOLDER_INSTAGRAM;
  const instagramHandle = (() => {
    if (!globals?.instagram_url) return PLACEHOLDER_INSTAGRAM_HANDLE;
    try { const m = new URL(globals.instagram_url).pathname.replace(/\/$/, "").split("/").pop(); return m ? `@${m}` : PLACEHOLDER_INSTAGRAM_HANDLE; } catch { return PLACEHOLDER_INSTAGRAM_HANDLE; }
  })();
  
  const servicesList = formServices || [];
  const methodsList = contactMethods || [];
  const prefixList = formPrefixes || [];

  // UI Strings
  const formStrings = uiStrings?.form || {};
  const feedbackStrings = uiStrings?.feedback || {};
  const detailStrings = uiStrings?.details || {};

  const [form, setForm] = useState({ ...DEFAULT_EMPTY_LEAD });

  useEffect(() => {
    if (prefixList.length > 0 && !form.phonePrefix) {
      setForm(f => ({ ...f, phonePrefix: prefixList[0].code }));
    }
  }, [prefixList]);

  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErrors(prev => ({ ...prev, [k]: false }));
    if (k === "email") setEmailError("");
  }

  async function onSubmit(e) {
    e.preventDefault();
    const missing = [];
    const errs = {};
    const fieldLabels = ["Nome", "Email", "Telefone", "Serviço", "Contacto", "Mensagem"];
    if (!form.name.trim()) { missing.push(fieldLabels[0]); errs.name = true; }
    if (!form.email.trim()) { missing.push(fieldLabels[1]); errs.email = true; }
    if (!form.phone.trim()) { missing.push(fieldLabels[2]); errs.phone = true; }
    if (!form.service) { missing.push(fieldLabels[3]); errs.service = true; }
    if (!form.contactMethod) { missing.push(fieldLabels[4]); errs.contactMethod = true; }
    if (!form.message.trim()) { missing.push(fieldLabels[5]); errs.message = true; }
    if (missing.length > 0) {
      setFieldErrors(errs);
      return;
    }
    if (!EMAIL_REGEX.test(form.email)) {
      setEmailError(VALIDATION_EMAIL_INVALID);
      return;
    }
    setLoading(true);
    try {
      await createLead({ ...form, phone: `${form.phonePrefix} ${form.phone}` });
      setDone(true);
      toast.success(feedbackStrings.success_title);
    } catch (err) {
      logger.error(err);
      toast.error(feedbackStrings.error_message);
    } finally {
      setLoading(false);
    }
  }

  const waDigits = whatsapp.replace(/\D/g, "");

  return (
    <section data-id="site-contact" id={sectionId} className="bg-offwhite py-28 md:py-40">
      <div data-id="contact-container" className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-16">
        <Reveal className="lg:col-span-5">
          <p data-id="contact-eyebrow" className="eyebrow text-warmgray">{eyebrow}</p>
          <h2 data-id="contact-headline" className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-softblack mt-4 md:mt-6 leading-[1.05]">
            {headline}
          </h2>
          <p data-id="contact-description" className="text-warmgray text-sm sm:text-base md:text-lg leading-relaxed mt-4 md:mt-8 font-light">
            {description}
          </p>
          {elementVisibility?.contact_info !== false && (
            <div data-id="contact-details-column" className="mt-8 md:mt-12 grid grid-cols-1 sm:grid-cols-1 gap-4 text-sm">
              <Detail icon={MessageCircle} label={detailStrings.whatsapp || "WhatsApp"} value={whatsapp} href={`https://wa.me/${waDigits}`} />
              <Detail icon={null} label="Instagram" value={instagramHandle} href={instagramUrl} isInstagram />
              <Detail icon={Mail} label={detailStrings.email || "E-mail"} value={contactEmail} href={`mailto:${contactEmail}`} />
              <Detail icon={Clock} label={detailStrings.atendimento || "Atendimento"} value={locations} />
            </div>
          )}
        </Reveal>

        {elementVisibility?.contact_form !== false && (
          <Reveal delay={200} className="lg:col-span-7">
          {done ? (
            <div data-id="contact-success" className="border border-fendi p-6 md:p-12 bg-white">
              <p data-id="contact-success-badge" className="eyebrow text-warmgray">{feedbackStrings.received_badge || ""}</p>
              <h3 data-id="contact-success-title" className="font-serif text-2xl sm:text-3xl md:text-4xl text-softblack mt-3 md:mt-4">
                {feedbackStrings.success_title}
              </h3>
              <p data-id="contact-success-message" className="text-warmgray mt-3 md:mt-4 text-sm md:text-base font-light">
                {feedbackStrings.success_message}
              </p>
              <button
                data-id="contact-reset"
                className="btn-ghost mt-6 md:mt-8"
                onClick={() => { setDone(false); setForm({ ...DEFAULT_EMPTY_LEAD }); setEmailError(""); }}
              >
                {feedbackStrings.send_new || ""}
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-id="contact-form" className="space-y-5 md:space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <label data-id="contact-label-name" className="block">
                  <span data-id="contact-label-text-name" className="eyebrow text-warmgray">{formStrings.name_label}</span>
                  <input
                    required minLength={2}
                    data-id="contact-input-name"
                    className={`editorial-field mt-3 ${fieldErrors.name ? "!border-b-destructive" : ""}`}
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder={formStrings.name_placeholder}
                  />
                </label>
                <label data-id="contact-label-email" className="block">
                  <span data-id="contact-label-text-email" className="eyebrow text-warmgray">{formStrings.email_label}</span>
                  <input
                    required type="email"
                    data-id="contact-input-email"
                    className={`editorial-field mt-3 ${emailError ? "!border-b-destructive" : ""}`}
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder={formStrings.email_placeholder}
                  />
                  {emailError && <p data-id="contact-email-error" className="text-destructive text-xs mt-2">{emailError}</p>}
                </label>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <label data-id="contact-label-phone" className="block">
                  <span data-id="contact-label-text-phone" className="eyebrow text-warmgray">{formStrings.phone_label}</span>
                    <div data-id="contact-phone-input-group" className="flex gap-3 mt-3 items-end w-full">
                      <div data-id="contact-prefix-wrapper" className="w-[20%] flex-shrink-0">
                        <PrefixSelector value={form.phonePrefix} onChange={(v) => update("phonePrefix", v)} prefixes={prefixList} />
                      </div>
                      <input
                        required
                        data-id="contact-input-phone"
                        className={`editorial-field w-[80%] ${fieldErrors.phone ? "!border-b-destructive" : ""}`}
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder={formStrings.phone_placeholder}
                      />
                    </div>
                </label>
                <label data-id="contact-label-service" className={`block ${fieldErrors.service ? "p-2 -m-2 border border-red-300 rounded-md" : ""}`}>
                  <span data-id="contact-label-text-service" className="eyebrow text-warmgray">{formStrings.service_label}</span>
                  <StyledSelect
                    required
                    data-id="contact-input-service"
                    value={form.service}
                    onChange={(v) => update("service", v)}
                    options={servicesList}
                    placeholder={formStrings.service_placeholder}
                  />
                </label>
              </div>
              <label data-id="contact-label-method" className={`block ${fieldErrors.contactMethod ? "p-2 -m-2 border border-red-300 rounded-md" : ""}`}>
                <span data-id="contact-label-text-method" className="eyebrow text-warmgray">{formStrings.contact_label}</span>
                <StyledSelect
                  data-id="contact-input-method"
                  value={form.contactMethod}
                  onChange={(v) => update("contactMethod", v)}
                  options={methodsList}
                  placeholder={formStrings.contact_placeholder || placeholder("short")}
                />
              </label>
              <label data-id="contact-label-message" className="block">
                <span data-id="contact-label-text-message" className="eyebrow text-warmgray">{formStrings.message_label}</span>
                <textarea
                  required
                  rows={4}
                  data-id="contact-input-message"
                  className={`editorial-field mt-3 ${fieldErrors.message ? "!border-b-destructive" : ""}`}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder={formStrings.message_placeholder}
                />
              </label>
              <div data-id="contact-submit-wrapper" className="pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  data-id="contact-submit"
                  className="btn-primary w-full sm:w-auto disabled:opacity-60"
                >
                  {loading ? formStrings.sending_button || placeholder("short") : formStrings.submit || placeholder("short")}
                </button>
              </div>
            </form>
          )}
        </Reveal>
        )}

      </div>
    </section>
  );
}

function Detail({ icon: Icon, label, value, href, isInstagram }) {
  const icon = isInstagram ? (
    <svg data-id={`contact-detail-icon-${label.toLowerCase()}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-warmgray shrink-0">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
    </svg>
  ) : Icon ? <Icon data-id={`contact-detail-icon-${label.toLowerCase()}`} size={16} className="text-warmgray shrink-0" /> : null;

  const content = (
    <div data-id={`contact-detail-inner-${label.toLowerCase()}`} className="flex items-center gap-3">
      {icon}
      <div data-id={`contact-detail-text-${label.toLowerCase()}`}>
        <p data-id={`contact-detail-label-${label.toLowerCase()}`} className="eyebrow text-warmgray">{label}</p>
        <p data-id={`contact-detail-value-${label.toLowerCase()}`} className="text-softblack mt-1 link-underline">{value}</p>
      </div>
    </div>
  );
  const isEmail = href?.startsWith("mailto:");
  return href ? (
    <a href={href} target={isEmail ? undefined : "_blank"} rel={isEmail ? undefined : "noopener noreferrer"} data-id={`contact-detail-${label.toLowerCase()}`}>{content}</a>
  ) : (
    <div data-id={`contact-detail-${label.toLowerCase()}`}>{content}</div>
  );
}
